/**
 * Auth Logic
 * ลอจิกเฉพาะของฟีเจอร์การยืนยันตัวตน
 *
 * Pure functions for auth-specific business logic.
 * ฟังก์ชันบริสุทธิ์สำหรับลอจิกทางธุรกิจเฉพาะ auth
 */

import type { RoleSlug } from '@/shared/types/permissions'

/**
 * Get the default redirect path after login based on user role.
 * ดึงเส้นทางเปลี่ยนเส้นทางเริ่มต้นหลังจากเข้าสู่ระบบตามบทบาทผู้ใช้
 */
export const getRedirectPath = (role: RoleSlug): string => {
  switch (role) {
    case 'platform_admin':
    case 'domain_admin':
      return '/settings/admin/users'
    case 'auditor':
      return '/'
    case 'viewer':
      return '/'
    default:
      return '/'
  }
}

/**
 * Route permission requirements.
 * ข้อกำหนดสิทธิ์ของเส้นทาง
 */
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/settings/admin/users': ['users:manage'],
  '/settings/admin/roles': ['roles:manage'],
  '/settings/admin/rbac': ['users:manage'],
  '/templates': ['templates:manage'],
  '/reports/configure': ['reports:create'],
  '/reports/search': ['reports:read'],
}

/**
 * Check if a user's role can access a route.
 * ตรวจสอบว่าบทบาทของผู้ใช้สามารถเข้าถึงเส้นทางได้หรือไม่
 */
export const getRoutePermissions = (pathname: string): string[] | null => {
  // Check exact match first
  const exact = ROUTE_PERMISSIONS[pathname]
  if (exact) return exact

  // Check prefix match (e.g. /admin/users/123 matches /admin/users)
  for (const [route, perms] of Object.entries(ROUTE_PERMISSIONS)) {
    if (pathname.startsWith(route)) return perms
  }

  return null // No specific permissions required
}

/**
 * Validate email format.
 * ตรวจสอบรูปแบบอีเมล
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}
